import { FormRow, UpdatePageLayout } from 'framework/ui/layout';
import { NumberInput, Dropdown, TextInput, CreateForm } from 'framework/form';
import { CrmAttachmentUploaderInput } from 'domain/shared/artifact/input';
import { useParams } from 'react-router-dom';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { HTTP } from 'framework/api';
import { useState } from 'react';
import {
  BILLING_TYPE_LABELS,
  SingleProjectType,
  useProjectFinanceService,
  useNewProjectUrl,
  BillingType,
} from 'domain/shared/newProject';
import { CURRENCY_TYPES } from 'domain/shared/core/constants';
import { InvoiceTotalInput } from 'domain/shared/core/invoice';
import { CurrencyDropdown } from 'domain/shared/core/input';

const LEGACY_BILLING_TYPE = 'LEGACY';
const PERIODIC_BILLING_TYPES = new Set([BillingType.PERIODIC_PREPAID, BillingType.PERIODIC_POSTPAID]);

export const getBillingTypeOptions = (currentBillingType?: string) => {
  const enabledBillingTypes = new Set(
    currentBillingType === LEGACY_BILLING_TYPE
      ? Object.keys(BILLING_TYPE_LABELS)
      : PERIODIC_BILLING_TYPES.has(currentBillingType as BillingType)
        ? Array.from(PERIODIC_BILLING_TYPES)
        : currentBillingType
          ? [currentBillingType]
          : Object.keys(BILLING_TYPE_LABELS),
  );

  return [
    ...Object.entries(BILLING_TYPE_LABELS).map(([value, label]) => ({
      value,
      label,
      disabled: !enabledBillingTypes.has(value),
    })),
    {
      value: LEGACY_BILLING_TYPE,
      label: 'Legacy',
      disabled: true,
    },
  ];
};

const UpdateBudgetLayout = () => {
  const { getFinanceUrl } = useNewProjectUrl();
  const { getById, getBudget, updateBudget, budgetsFinanceSearch, getUpdateBudgetLayoutValidationSchema } =
    useProjectFinanceService();
  const { id } = useParams();
  const { addMessage } = useMessageService();
  const [currentBillingType, setCurrentBillingType] = useState<string>();
  const [budgetId, setBudgetId] = useState<number>();

  const createDefaultBudgetData = (projectData: any) => {
    return {
      id: projectData?.defaultBudgetId || null,
      sowId: '',
      projectName: projectData?.name || '',
      billingType: BillingType.MILESTONE,
      fte: 0,
      hours: 0,
      amount: null,
      currency: null,
      relatedDocuments: [],
      structure: [],
      finance: null,
    };
  };

  const getFormData = async () =>
    getById(id!).then(async (projectData: SingleProjectType) => {
      if (!projectData?.defaultBudgetId) {
        addMessage(new ErrorMessage('Budget not found.'));
        return [undefined, undefined];
      }
      setBudgetId(projectData.defaultBudgetId);
      return Promise.all([
        getBudget(projectData.defaultBudgetId).catch(e => {
          if ((e as Response).status === HTTP.NOT_FOUND) {
            addMessage(new ErrorMessage('Budget not found.'));
            return undefined;
          }
          throw e;
        }),
        budgetsFinanceSearch({
          page: 0,
          size: 1,
          sort: { createdAt: 'ASC' },
          filter: { id: { eq: projectData.defaultBudgetId } },
        }).then(response => response?.result?.[0]),
      ]).then(([budgetResponse, financeResponse]) => {
        if (!budgetResponse && !financeResponse) {
          const defaultBudgetData = createDefaultBudgetData(projectData);
          setCurrentBillingType(defaultBudgetData.billingType);
          return defaultBudgetData;
        }

        const budgetSOW = budgetResponse?.structure?.find(item => item.source === 'SOW');
        const financeSOW = financeResponse?.structure?.find(item => item.source === 'SOW');

        const formData = {
          id: projectData.defaultBudgetId,
          sowId: budgetResponse?.sowId,
          projectName: budgetResponse?.projectName || projectData.name || '',
          billingType: budgetResponse?.billingType || financeResponse?.billingType,
          fte: budgetResponse?.fte || 0,
          hours: budgetSOW?.hours || 0,
          amount: financeSOW?.amount || budgetSOW?.amount || null,
          currency: financeSOW?.currency || budgetSOW?.currency || null,
          relatedDocuments: budgetResponse?.relatedDocuments || [],
        };

        setCurrentBillingType(formData.billingType);
        return formData;
      });
    });

  return (
    <>
      <CreateForm
        key="budget-data"
        afterSubmit={{
          redirect: getFinanceUrl(),
          message: 'You are awesome! Budget information has been successfully updated.',
        }}
        getValidationSchemaFunc={getUpdateBudgetLayoutValidationSchema}
        submitFormFunc={data => updateBudget(data.id, data)}
        getDataFunc={getFormData}
      >
        <UpdatePageLayout title="Edit Budget Information" isCancelButtonVisible>
          <FormRow spacing={1} sx={{ pt: 2 }}>
            <TextInput source="sowId" label="SOW ID" />
            <TextInput source="projectName" label="Project name" />
            <Dropdown
              placeholder="Select options"
              options={getBillingTypeOptions(currentBillingType)}
              source="billingType"
              label="Billing type"
              filterOption={option => !option.data.disabled}
            />
            <NumberInput source="fte" label="FTE, People" />
          </FormRow>
          <FormRow spacing={1}>
            <NumberInput source="hours" label="Estimated hours" />
            <InvoiceTotalInput allowNegative source="amount" currencySource="currency" label="Amount" />
            <CurrencyDropdown label="Currency" source="currency" options={CURRENCY_TYPES} />
          </FormRow>
          <FormRow>
            <CrmAttachmentUploaderInput label="Related documents" source="relatedDocuments" budgetId={budgetId} />
          </FormRow>
        </UpdatePageLayout>
      </CreateForm>
    </>
  );
};

export default UpdateBudgetLayout;
